import { lazy, Suspense, type ReactNode } from "react";
import { Routes, Route, Outlet, useNavigate, useParams, Navigate } from "react-router-dom";
import { ErrorPage } from "@safers/ui/templates";
import { ProtectedRouter, logout, useAuthStore, selectUser, canAccessSite } from "@safers/auth";
import { Spinner } from "@safers/ui/atoms";

import { DashboardPage, LoginPage } from "@/pages";

const SitePage = lazy(() => import("@/pages/site").then((m) => ({ default: m.SitePage })));
const SiteBabylonPage = lazy(() =>
  import("@/deferred/babylon/site-babylon").then((m) => ({ default: m.SiteBabylonPage }))
);
const CCTVAIPage = lazy(() => import("@/pages/cctv-ai").then((m) => ({ default: m.CCTVAIPage })));
const CCTVPlaybackPage = lazy(() =>
  import("@/pages/cctv-playback").then((m) => ({ default: m.CCTVPlaybackPage }))
);
const POC3DPage = lazy(() => import("@/pages/poc-3d").then((m) => ({ default: m.POC3DPage })));
const POC3DBabylonPage = lazy(() =>
  import("@/deferred/babylon/poc-3d-babylon").then((m) => ({ default: m.POC3DBabylonPage }))
);

function LazyFallback() {
  return (
    <div className="flex h-screen w-screen items-center justify-center">
      <Spinner size="lg" />
    </div>
  );
}

function NotFoundPage() {
  const navigate = useNavigate();
  return (
    <ErrorPage variant="404" primaryAction={{ label: "홈으로", onClick: () => navigate("/") }} />
  );
}

function ForbiddenPage() {
  const navigate = useNavigate();
  // 403 도달자는 이미 로그인된 상태이므로 "다시 로그인" 대신 "로그아웃"이 명확.
  // 다른 계정으로 전환하려면 로그아웃 후 /login으로 이동시킨다.
  const handleLogout = async () => {
    try {
      await logout();
    } catch {
      // 서버 실패해도 클라이언트 세션은 정리
    } finally {
      useAuthStore.getState().reset();
      navigate("/login", { replace: true });
    }
  };
  return (
    <ErrorPage
      variant="403"
      primaryAction={{ label: "홈으로", onClick: () => navigate("/") }}
      secondaryAction={{ label: "로그아웃", onClick: handleLogout }}
    />
  );
}

function SiteGate({ param, children }: { param: "id" | "siteId"; children: ReactNode }) {
  const params = useParams();
  const user = useAuthStore(selectUser);
  const siteId = params[param];
  if (siteId != null && !canAccessSite(user, siteId)) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

function AuthLayout() {
  return (
    <ProtectedRouter
      fallback={
        <div className="flex h-screen w-screen items-center justify-center">
          <Spinner size="lg" />
        </div>
      }
    >
      <Outlet />
    </ProtectedRouter>
  );
}

export function AppRoutes() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/poc-3d"
        element={
          <Suspense fallback={<LazyFallback />}>
            <POC3DPage />
          </Suspense>
        }
      />
      <Route
        path="/poc-3d-babylon"
        element={
          <Suspense fallback={<LazyFallback />}>
            <POC3DBabylonPage />
          </Suspense>
        }
      />
      {/* USER가 admin URL 직접 진입 시 admin AuthorityGate가 이 경로로 redirect */}
      <Route path="/403" element={<ForbiddenPage />} />
      <Route path="/404" element={<NotFoundPage />} />

      {/* Protected — 인증 필요 */}
      <Route element={<AuthLayout />}>
        <Route index element={<DashboardPage />} />
        <Route
          path="site/:id"
          element={
            <SiteGate param="id">
              <Suspense fallback={<LazyFallback />}>
                <SitePage />
              </Suspense>
            </SiteGate>
          }
        />
        <Route
          path="site-babylon/:id"
          element={
            <SiteGate param="id">
              <Suspense fallback={<LazyFallback />}>
                <SiteBabylonPage />
              </Suspense>
            </SiteGate>
          }
        />
        <Route
          path="cctv-ai/:siteId"
          element={
            <SiteGate param="siteId">
              <Suspense fallback={<LazyFallback />}>
                <CCTVAIPage />
              </Suspense>
            </SiteGate>
          }
        />
        <Route
          path="cctv-playback/:siteId"
          element={
            <SiteGate param="siteId">
              <Suspense fallback={<LazyFallback />}>
                <CCTVPlaybackPage />
              </Suspense>
            </SiteGate>
          }
        />
      </Route>

      {/* 404 */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
